/**
 * Vuex store
 *
 * 還原自 chunk-common.js（模組定義於 pretty 檔 L355-L565）。
 * state / actions / mutations / getters 四段與原始 bundle 逐項對應。
 */

import Vue from 'vue'
import Vuex from 'vuex'
import * as types from './mutation-types'
import { setStorage } from '../utils'

Vue.use(Vuex)

const KEY = {
  cartList: '5ew41gf56ew456-cart-list',
  userName: '5ew41gf56ew456-user-name',
  userNumber: '5ew41gf56ew456-user-number',
  remark: '5ew41gf56ew456-remark',
  orderId: '5ew41gf56ew456-order-id',
  clientId: '5ew41gf56ew456-client-id',
  discount: '5ew41gf56ew456-discount',
  location: '5ew41gf56ew456-location',
  orderType: '5ew41gf56ew456-order-type',
  language: '5ew41gf56ew456-language'
}

const HOUR = 3600
const MONTH = 2592000

const state = {
  lang: 'tw',
  scroll: 0,
  close: true,
  setup_time: 0,
  /*
   * 今日營業時間，格式 HHMM，來自 /api/storeStatus。
   * null = 尚未取得；{open: null, close: null} = 今日公休。
   * 後端的 BUSINESS_HOURS 是唯一來源，前端不自己寫死一份。
   */
  business_hours: null,
  sel_product: null,
  product: null,
  name: '',
  number: '',
  remark: '',
  client_id: null,
  order_id: null,
  discount: null,
  cart_list: [],
  location: '',
  order_type: types.ORDER_TYPE_PICK_UP,
  open_cart: false
}

const actions = {
  setLanguage:     ({ commit }, v) => commit(types.LANGUAGE, v),
  setScroll:       ({ commit }, v) => commit(types.SCROLL, v),
  setClose:        ({ commit }, v) => commit(types.CLOSE, v),
  setStoreStatus:  ({ commit }, v) => commit(types.STORE_STATUS, v),
  setSelProduct:   ({ commit }, v) => commit(types.SEL_PRODUCT, v),
  addItemToCart:   ({ commit }, v) => commit(types.CART_ADD, v),
  editItemToCart:  ({ commit }, v) => commit(types.CART_EDIT, v),
  removeItemToCart:({ commit }, v) => commit(types.CART_DELETE, v),
  setProduct:      ({ commit }, v) => commit(types.PRODUCT, v),
  setUserName:     ({ commit }, v) => commit(types.NAME_SET, v),
  setUserNumber:   ({ commit }, v) => commit(types.NUMBER_SET, v),
  setRemark:       ({ commit }, v) => commit(types.REMARK_SET, v),
  setClientID:     ({ commit }, v) => commit(types.CLIENT_ID, v),
  setOrderID:      ({ commit }, v) => commit(types.ORDER_ID, v),
  setDiscount:     ({ commit }, v) => commit(types.DISCOUNT_SET, v),
  setOrderType:    ({ commit }, v) => commit(types.ORDER_TYPE_SET, v),
  setLocation:     ({ commit }, v) => commit(types.LOCATION_SET, v),
  setOpenCart:     ({ commit }, v) => commit(types.OPEN_CART, v),

  deleteAll: () => {
    localStorage.removeItem(KEY.cartList)
    localStorage.removeItem(KEY.userName)
    localStorage.removeItem(KEY.userNumber)
    localStorage.removeItem(KEY.remark)
    localStorage.removeItem(KEY.orderId)
    localStorage.removeItem(KEY.clientId)
    localStorage.removeItem(KEY.discount)
  }
}

const mutations = {
  // 語言是唯一真正寫進 cookie 的項目，其餘都用帶有效期的 localStorage
  [types.LANGUAGE](state, v) {
    state.lang = v
    Vue.$cookies.set(KEY.language, state.lang)
  },

  [types.SCROLL](state, v) { state.scroll = v },
  [types.CLOSE](state, v) { state.close = v },
  [types.SEL_PRODUCT](state, v) { state.sel_product = v },
  [types.PRODUCT](state, v) { state.product = v },
  [types.OPEN_CART](state, v) { state.open_cart = v },

  // 來自 /api/storeStatus 的回應：close 是 storeIsOpen 的反相
  [types.STORE_STATUS](state, v) {
    state.close = !v.storeIsOpen
    state.setup_time = v.storeSetupTime
    state.business_hours = { open: v.storeOpenHour, close: v.storeCloseHour }
  },

  [types.CART_SET](state, v) { state.cart_list = v },

  [types.CART_ADD](state, v) {
    state.cart_list.push(v)
    setStorage(KEY.cartList, JSON.stringify(state.cart_list), HOUR)
  },

  // 兩個 mutation 都加了 i === -1 的防護。原始碼沒有這道檢查，
  // 一旦 findIndex 找不到就會 splice(-1, 1) 刪掉最後一筆、或
  // cart_list[-1] = v 寫出一個不會被渲染的屬性 —— 兩種都是靜默的資料損壞。
  // 呼叫端傳錯 id 時，寧可什麼都不做並留下 log，也不要動到無關的品項。
  [types.CART_EDIT](state, v) {
    const i = state.cart_list.findIndex((item) => item.id === v.id)

    if (i === -1) {
      console.warn('[cart] CART_EDIT 找不到對應項目，已略過', v.id)
      return
    }

    state.cart_list.splice(i, 1, v)
    setStorage(KEY.cartList, JSON.stringify(state.cart_list), HOUR)
  },

  [types.CART_DELETE](state, id) {
    const i = state.cart_list.findIndex((item) => item.id === id)

    if (i === -1) {
      console.warn('[cart] CART_DELETE 找不到對應項目，已略過', id)
      return
    }

    state.cart_list.splice(i, 1)
    setStorage(KEY.cartList, JSON.stringify(state.cart_list), HOUR)
  },

  [types.NAME_SET](state, v) {
    state.name = v
    setStorage(KEY.userName, state.name, HOUR)
  },

  /*
   * 訂單備註（鍵名沿用 unified_compilation）。
   *
   * 原始版本不保存這個欄位 —— 從結帳頁離開再回來就消失，
   * LINE Pay 取消付款導回時也一樣。姓名與電話都有保存，唯獨備註沒有。
   * 那個欄位當初標示為「統編」，多半被當成可有可無；改標示為
   * 「訂單備註」並實際用於外送指示之後，掉內容就是明顯的困擾。
   *
   * 期限與姓名電話一致（1 小時）。
   */
  [types.REMARK_SET](state, v) {
    state.remark = v
    setStorage(KEY.remark, state.remark, HOUR)
  },

  [types.NUMBER_SET](state, v) {
    state.number = v
    setStorage(KEY.userNumber, state.number, HOUR)
  },

  [types.CLIENT_ID](state, v) {
    state.client_id = v
    setStorage(KEY.clientId, state.client_id, HOUR)
  },

  [types.ORDER_ID](state, v) {
    state.order_id = v
    setStorage(KEY.orderId, state.order_id, HOUR)
  },

  [types.DISCOUNT_SET](state, v) {
    state.discount = v
    setStorage(KEY.discount, state.discount, HOUR)
  },

  // 外送方式與地址保留一個月，其餘只留一小時
  [types.ORDER_TYPE_SET](state, v) {
    state.order_type = v
    setStorage(KEY.orderType, state.order_type, MONTH)
  },

  [types.LOCATION_SET](state, v) {
    state.location = v
    setStorage(KEY.location, state.location, MONTH)
  }
}

const getters = {
  getLanguage:   (s) => s.lang,
  getScroll:     (s) => s.scroll,
  getClose:      (s) => s.close,
  getSetupTime:  (s) => s.setup_time,
  getBusinessHours: (s) => s.business_hours,
  getSelProduct: (s) => s.sel_product,
  getCartList:   (s) => s.cart_list,
  getProduct:    (s) => s.product,
  getUserName:   (s) => s.name,
  getUserNumber: (s) => s.number,
  getRemark:     (s) => s.remark,
  getClientID:   (s) => s.client_id,
  getOrderID:    (s) => s.order_id,
  getDiscount:   (s) => s.discount,
  getOrderType:  (s) => s.order_type,
  getLocation:   (s) => s.location,
  getOpenCart:   (s) => s.open_cart
}

export default new Vuex.Store({ state, actions, mutations, getters })
